import React, { useState } from "react";
import { Link } from "react-router-dom";
import { Globe, Linkedin } from "lucide-react";

const LINKEDIN_URL = "https://www.linkedin.com/company/worldaiinfo/";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";

const COLS = [
  {
    title: "Platform",
    links: [
      ["Tools", "/tools"], ["Jobs", "/jobs"], ["Events", "/events"], ["News", "/news"], ["Courses", "/courses"], ["Side Hustles", "/side-hustles"], ["AI Data & Statistics", "/ai-progress"], ["AI in Your Job", "/ai-in-your-job"], ["For Recruiters", "/recruiters"],
    ],
  },
  {
    title: "Company",
    links: [
      ["About Us", "/about"], ["Advertise", "/advertise"], ["Data Sources", "/sources"], ["Submit a Tool", "/register"], ["Post a Job", "/jobs"], ["Contact", "/contact"],
    ],
  },
  {
    title: "Legal",
    links: [
      ["Privacy Policy", "/privacy"], ["Terms of Service", "/terms"], ["Cookie Policy", "/cookies"], ["Disclaimer", "/disclaimer"],
    ],
  },
];

export default function Footer() {
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);

  const subscribe = async (e) => {
    e.preventDefault();
    if (!email) return;
    setBusy(true);
    try {
      await api.post("/newsletter/subscribe", { email });
      toast.success("Subscribed! Watch for the WorldAI Weekly every Friday.");
      setEmail("");
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail));
    } finally { setBusy(false); }
  };

  return (
    <footer className="border-t border-white/10 mt-24" data-testid="footer">
      <div className="max-w-7xl mx-auto px-6 md:px-12 py-14">
        {/* Top row */}
        <div className="mb-10">
          <div className="flex items-center gap-2 mb-2">
            <Globe className="w-6 h-6 text-[#00F0FF]" />
            <span className="font-display font-extrabold text-lg">World<span className="text-[#00F0FF]">AI</span>Info</span>
          </div>
          <p className="text-sm text-[#94A3B8]">The world's AI intelligence hub</p>
          <a href={LINKEDIN_URL} target="_blank" rel="noopener noreferrer" aria-label="Follow WorldAIInfo on LinkedIn"
            data-testid="footer-linkedin"
            className="mt-4 inline-flex items-center gap-2 text-sm text-[#94A3B8] border border-white/10 rounded-md px-3 py-2 hover:border-[#00F0FF]/50 hover:text-[#00F0FF] transition-colors">
            <Linkedin className="w-4 h-4" /> Follow us on LinkedIn
          </a>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
          {COLS.map((col) => (
            <div key={col.title}>
              <p className="font-mono-tech text-xs uppercase text-[#94A3B8] mb-3">{col.title}</p>
              <ul className="space-y-2 text-sm text-[#94A3B8]">
                {col.links.map(([label, to]) => (
                  <li key={label}>
                    <Link to={to} className="hover:text-[#00F0FF] transition-colors" data-testid={`footer-link-${label.toLowerCase().replace(/\s+/g, "-")}`}>{label}</Link>
                  </li>
                ))}
              </ul>
            </div>
          ))}

          {/* Newsletter column */}
          <div className="col-span-2 md:col-span-1">
            <p className="font-mono-tech text-xs uppercase text-[#94A3B8] mb-3">Newsletter</p>
            <p className="text-sm text-[#94A3B8] mb-3">Get the WorldAI Weekly — real, sourced AI data and the week's most important shifts, every Friday. Free.</p>
            <form onSubmit={subscribe} className="flex flex-col gap-2" data-testid="footer-newsletter">
              <input type="email" required value={email} onChange={(e) => setEmail(e.target.value)}
                placeholder="you@email.com" data-testid="footer-newsletter-email"
                className="w-full bg-[#111827] border border-white/10 rounded-md px-3 py-2.5 text-sm focus:border-[#00F0FF] focus:outline-none" />
              <button type="submit" disabled={busy} data-testid="footer-newsletter-submit"
                className="bg-[#00F0FF] text-[#04121a] font-semibold px-4 py-2.5 rounded-md hover:brightness-110 transition disabled:opacity-50 text-sm">
                {busy ? "Subscribing…" : "Subscribe"}
              </button>
            </form>
          </div>
        </div>
      </div>

      {/* Bottom bar */}
      <div className="border-t border-white/10">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-5 flex flex-col sm:flex-row items-center justify-between gap-3">
          <p className="text-xs text-[#64748B]">Copyright 2026 WorldAIInfo.com — All rights reserved</p>
          <div className="flex items-center gap-4 text-xs text-[#64748B]">
            <Link to="/privacy" className="hover:text-[#00F0FF] transition-colors">Privacy Policy</Link>
            <span>—</span>
            <Link to="/terms" className="hover:text-[#00F0FF] transition-colors">Terms</Link>
            <span>—</span>
            <Link to="/cookies" className="hover:text-[#00F0FF] transition-colors">Cookies</Link>
            <span>—</span>
            <Link to="/disclaimer" className="hover:text-[#00F0FF] transition-colors">Disclaimer</Link>
          </div>
        </div>
      </div>
    </footer>
  );
}
